/**
 * Shared bits for search engines and AI answer engines: the canonical origin, absolute URLs
 * and the structured data for the home page, the pricing page and blog posts.
 */
export const SITE = 'https://www.monet.io';
export const LOGO = `${SITE}/icon-512.png`;

/** Structured data as a JSON string safe to inline in a script tag. */
const ld = (data) => JSON.stringify(data).replace(/</g, '\\u003c');

const ORGANIZATION = {
  '@type': 'Organization',
  '@id': `${SITE}/#organization`,
  name: 'Monet',
  legalName: 'Monet Gallery Inc.',
  url: SITE,
  logo: LOGO,
  email: 'info@monet.io',
  sameAs: ['https://www.linkedin.com/company/themonet', 'https://www.instagram.com/themonet_co/'],
};

// One product entity, described on the home page and priced on the pricing page.
const SOFTWARE = {
  '@type': 'SoftwareApplication',
  '@id': `${SITE}/#software`,
  name: 'Monet',
  applicationCategory: 'DesignApplication',
  operatingSystem: 'Web browser',
  url: SITE,
  description: 'Software for event designers to speed up their creative process. Lay out a show floor inside your real venue, to scale, and export DWG, PDF or GLB.',
  publisher: { '@id': ORGANIZATION['@id'] },
};

/** The company, the site and the product, for the home page. */
export const homeJsonLd = () => ld({
  '@context': 'https://schema.org',
  '@graph': [
    ORGANIZATION,
    { '@type': 'WebSite', '@id': `${SITE}/#website`, url: SITE, name: 'Monet', publisher: { '@id': ORGANIZATION['@id'] } },
    SOFTWARE,
  ],
});

/** The product with every plan and credit pack as an offer, and the FAQ, for the pricing page. */
export const pricingJsonLd = (plans, packs, faqs) => ld({
  '@context': 'https://schema.org',
  '@graph': [
    {
      ...SOFTWARE,
      offers: [
        ...plans.map((p) => ({
          '@type': 'Offer',
          name: `${p.name} plan`,
          description: `${p.credits.toLocaleString('en-US')} AI credits a month` +
            (p.trialDays ? `; ${p.trialDays}-day free trial with ${p.trialCredits} credits` : ''),
          price: p.monthly,
          priceCurrency: 'USD',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: p.monthly, priceCurrency: 'USD', billingDuration: 'P1M' },
          url: `${SITE}/pricing`,
        })),
        ...packs.map((c) => ({
          '@type': 'Offer',
          name: `${c.credits.toLocaleString('en-US')} AI credits`,
          description: 'One-time credit pack',
          price: c.price,
          priceCurrency: 'USD',
          url: `${SITE}/pricing`,
        })),
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
    },
  ],
});

/** An absolute URL for a path or an already absolute URL. */
export const absolute = (u) => (!u ? '' : /^https?:\/\//.test(u) ? u : `${SITE}${u.startsWith('/') ? '' : '/'}${encodeURI(u)}`);

/** The canonical address of a blog post. */
export const postUrl = (slug) => `${SITE}/category/blog/${slug}`;

/** schema.org BlogPosting for one post, as a JSON string safe to inline in a script tag. */
export function postJsonLd(post) {
  const published = post.publishedAt || post.createdAt || undefined;
  const data = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: (post.title || '').slice(0, 110),
    description: post.excerpt || undefined,
    image: post.featuredImage?.[0]?.url ? [absolute(post.featuredImage[0].url)] : undefined,
    datePublished: published,
    dateModified: post.updatedAt || published,
    author: { '@type': 'Person', name: post.author?.name || 'Juliano Wahab' },
    publisher: { '@type': 'Organization', name: 'Monet', url: SITE, logo: { '@type': 'ImageObject', url: LOGO } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': postUrl(post.slug) },
    articleSection: post.categories?.[0]?.name || undefined,
    keywords: (post.categories || []).map((c) => c.name).join(', ') || undefined,
  };
  return ld(data);
}
